/**
 *
 * Common Buttons
 *
 */
import React, { FC, ReactNode, forwardRef, useEffect, useState } from 'react';

import {
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
  Center,
} from '@chakra-ui/react';
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import { MdAdd } from 'react-icons/md';
import { colorOpacity, customTheme } from 'theme';

import {
  AddOrCreateIcon,
  CopyIcon,
  customColorButtonStyles,
  OutlinedIconButton,
  TrashIcon,
} from 'app/components';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { PrimaryButtonProps } from 'components/DataEntry/Button/types';
import { DownloadIcon } from 'components/Icons';

import {
  CheckmarkCircleIcon,
  EditIcon,
  FilterIcon,
  LeftArrowIcon,
  RefreshIcon,
} from '../../Icons';

import {
  deleteButtonStyles,
  editButtonStyles,
  grayIconButtonStyles,
  primaryButtonStyles,
  primaryIconButtonStyles,
  cancelButtonStyles,
  passwordFieldIconStyles,
} from './styles';

//import show = Mocha.reporters.Base.cursor.show;

interface Props extends ButtonProps {
  label?: string | ReactNode;
}

interface CProps extends Props {
  showIcon?: boolean;
  buttonType: 'save' | 'add' | 'delete' | 'edit' | 'cancel' | 'otherColor';
  customColor?: string; //in case otherColor button type is provided
}

export const DownloadButton: React.FC<Props> = props => {
  return (
    <Button
      {...primaryButtonStyles}
      /*color={'green'}
      borderColor={'green'}
      _hover={{ bg: 'green', color: '#fff', borderColor: '#fff' }}
      _focus={{ bg: 'green', color: '#fff', borderColor: '#fff' }}
      _active={{ bg: 'green', color: '#fff', borderColor: '#fff' }}*/
      rightIcon={<DownloadIcon />}
      {...props}
    >
      Download
    </Button>
  );
};

export const SaveButton: React.FC<Props> = props => {
  return <CustomButton buttonType="save" {...props} showIcon={false} />;
};

export const AddButton: React.FC<Props> = props => {
  return (
    <OutlinedIconButton leftIcon={<AddOrCreateIcon />} {...props}>
      {props?.label}
    </OutlinedIconButton>
  );
};

export const DeleteButton: React.FC<Props> = props => {
  return <CustomButton buttonType="delete" {...props} />;
};

export const CancelButton: React.FC<Props> = props => {
  return <CustomButton buttonType="cancel" showIcon={false} {...props} />;
};

export const EditButton: React.FC<Props> = props => {
  return <CustomButton buttonType="edit" {...props} />;
};

export const BackIconButton: React.FC<ButtonProps> = props => {
  const { ...rest } = props;
  return (
    <Button {...primaryIconButtonStyles} bg="white" color="primary" {...rest}>
      <LeftArrowIcon />
    </Button>
  );
};

export const RefreshIconButton: React.FC<ButtonProps> = props => {
  const { ...rest } = props;
  return (
    <Button {...grayIconButtonStyles} {...rest}>
      <RefreshIcon size={18} />
    </Button>
  );
};

export const PrimaryButton = forwardRef<HTMLButtonElement, PrimaryButtonProps>(
  (props, ref) => {
    const {
      tooltip,
      leftIcon,
      rightIcon,
      iconSize = 5,
      onMouseLeave,
      onMouseEnter,
      showGrayBg = false,
      tooltipStyles,
      iconColor,
      ...rest
    } = props;
    const [isHovered, setIsHovered] = useState(false);

    const renderIcon = icon => (
      <Center w={iconSize} h={iconSize} color={iconColor || 'inherit'}>
        {icon}
      </Center>
    );

    return (
      <CustomTooltip label={tooltip} styles={tooltipStyles}>
        <Button
          ref={ref}
          {...primaryButtonStyles}
          bg={
            showGrayBg
              ? customTheme.colors.gray['50']
              : colorOpacity(customTheme.colors.primary, 0.2)
          }
          borderColor={showGrayBg ? customTheme.colors.gray['300'] : 'primary'}
          color={showGrayBg ? customTheme.colors.gray['300'] : 'primary'}
          leftIcon={leftIcon && renderIcon(leftIcon)}
          rightIcon={rightIcon && renderIcon(rightIcon)}
          onMouseEnter={e => {
            setIsHovered(true);
            onMouseEnter?.(e);
          }}
          onMouseLeave={e => {
            setIsHovered(false);
            onMouseLeave?.(e);
          }}
          {...rest}
        >
          {props.children}
        </Button>
      </CustomTooltip>
    );
  },
);

export const FilterButton: FC<ButtonProps> = props => (
  <PrimaryButton leftIcon={<FilterIcon />} {...props}>
    Filters
  </PrimaryButton>
);

export interface PrimaryIconButtonProps extends IconButtonProps {
  tooltip?: string;
  tooltipW?: string | number;
  showGrayBg?: boolean;
  showGrayIcon?: boolean;
}

export const PrimaryIconButton: FC<PrimaryIconButtonProps> = props => {
  const { tooltip, tooltipW = 24, showGrayBg, showGrayIcon, ...rest } = props;
  return (
    <CustomTooltip w={tooltipW} textAlign="center" label={tooltip}>
      <IconButton
        {...primaryButtonStyles}
        p={1.5}
        borderRadius="xl"
        maxW={6}
        bg={
          showGrayBg
            ? customTheme.colors.gray['50']
            : showGrayIcon
            ? 'white'
            : 'primary'
        }
        color={
          showGrayBg || showGrayIcon ? customTheme.colors.gray['250'] : 'white'
        }
        {...rest}
      />
    </CustomTooltip>
  );
};

export const DownloadIconButton: FC<
  IconButtonProps & { tooltip?: string }
> = props => {
  return (
    <PrimaryIconButton tooltip="Download" p={1} {...props}>
      {!props.isLoading && <DownloadIcon />}
    </PrimaryIconButton>
  );
};

export const PasswordFieldIconButton: FC<
  {
    show: boolean;
    toggleShowPassword: () => void;
  } & ButtonProps
> = props => {
  const { show, toggleShowPassword, ...rest } = props;
  return (
    <Button
      {...passwordFieldIconStyles}
      onClick={() => toggleShowPassword()}
      {...rest}
    >
      {show ? <BsEye /> : <BsEyeSlash />}
    </Button>
  );
};

export const CopyToClipboardIconButton: FC<
  {
    show?: boolean;
    value?: string;
    onClick?: () => void;
    tooltip?: string;
    iconSize?: number;
  } & ButtonProps
> = props => {
  const {
    show = true,
    value = '',
    onClick,
    tooltip,
    iconSize = 5,
    ...rest
  } = props;
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    setIsCopied(false);
  }, [value]);

  const handleClick = () => {
    try {
      const copyResult = onClick
        ? onClick()
        : navigator.clipboard.writeText((value ?? '').toString());

      Promise.resolve(copyResult)
        .then(() => setIsCopied(true))
        .catch(() => setIsCopied(false));
    } catch {
      setIsCopied(false);
    }
  };

  return (
    <CustomTooltip
      w={24}
      textAlign="center"
      label={isCopied ? 'Copied to clipboard' : tooltip ?? 'Copy to clipboard'}
    >
      <Button
        {...passwordFieldIconStyles}
        onClick={handleClick}
        {...rest}
        {...(isCopied
          ? {
              color: customTheme.colors.green['400'],
              _active: { transform: 'scale(0.90)' },
            }
          : { _active: { transform: 'scale(0.90)' } })}
      >
        {isCopied ? (
          <CheckmarkCircleIcon w={iconSize} h={iconSize} />
        ) : (
          <CopyIcon w={iconSize} h={iconSize} />
        )}
      </Button>
    </CustomTooltip>
  );
};

export const CustomButton: FC<CProps> = props => {
  const {
    label,
    buttonType,
    customColor,
    showIcon = true,
    leftIcon,
    children,
    ...rest
  } = props;
  const styles = {
    save: primaryButtonStyles,
    add: primaryButtonStyles,
    delete: deleteButtonStyles,
    edit: editButtonStyles,
    cancel: cancelButtonStyles,
    otherColor: customColorButtonStyles(customColor),
  };
  const defaultLabel = {
    save: 'SAVE',
    add: 'ADD',
    delete: 'DELETE',
    edit: 'EDIT',
    cancel: 'CANCEL',
  };
  const icon = {
    add: <MdAdd />,
    delete: <TrashIcon />,
    edit: <EditIcon />,
  };
  return (
    <Button
      {...styles[buttonType]}
      leftIcon={
        showIcon || leftIcon ? (
          <Center w={4} h={4}>
            {leftIcon ?? icon[buttonType]}
          </Center>
        ) : undefined
      }
      {...rest}
    >
      {label || children || defaultLabel[buttonType]}
    </Button>
  );
};
